import * as React from "react";
import { useState, useEffect } from 'react';
import { Outlet, ScrollRestoration, useParams } from "react-router-dom";
import * as MapDisplayMode from "../constants/mapdisplaymode";
import * as MapType from "../constants/maptype";
import { IMapOptions } from "../interfaces/mapoptions";
import { LoadingState } from "../interfaces/loading";
import { useAppSelector, useAppDispatch } from "../store/index";
import { RootState } from "../reducers/index";
import { fetchPois, selectMapDisplayMode, showPoi, showResults } from "../reducers/poi";
import { setlocationName, setlocationNameLong } from "../reducers/halfway";
import { PoiSearch } from '../api/poisearch';
import AdthriveLeaderboard from "./adthriveleaderboard";
import AdthriveContent from "./adthrivecontent";
import Breadcrumb from "./breadcrumb";
import ErrorList from "./errorlist";
import GoogleMapResults from "./googlemapresults";
import MapKitResults from "./mapkitresults";
import GoogleMapStatic from "./googlemapstatic";
import HeaderSeo from "./headerseo";
import PoiListError from "./poilisterror";
import StartEnd from "./startend";
import MetaDataResults from "./metadataresults";
import RightResults from "./rightresults";

type PoiParams = {
    poiId: string;
}

const MapContainerResults = () => {
    const dispatch = useAppDispatch();
    const [mapErrors, setMapErrors] = useState([]);
    const [hasMapError, setHasMapError] = useState(false);
    const mapoptions = useAppSelector<IMapOptions>((x: RootState) => x.mapoptions);
    const mapDisplayMode = useAppSelector<number>(selectMapDisplayMode);
    const { poiId } = useParams<PoiParams>();
    const poiList = useAppSelector<Array<PoiSearch>>((x: RootState) => x.poi.list);
    const poiStatus = useAppSelector<LoadingState>((x: RootState) => x.poi.status);
    const locationName = useAppSelector<string>((x: RootState) => x.halfway.locationName);
    const locationNameLong = useAppSelector<string>((x: RootState) => x.halfway.locationNameLong);
    const halfwayLatLng = useAppSelector<google.maps.LatLngLiteral>((x: RootState) => x.halfway.latLng);

    // When the latlng appears then it's OK to download the pois
    useEffect(() => {
        if (halfwayLatLng && poiStatus == 'idle') {
            // never loaded the pois... time to load them!
            dispatch(fetchPois({ latlng: halfwayLatLng, term: mapoptions.poi, token: mapoptions.token }));
        }
    }, [halfwayLatLng, poiStatus]);

    // fixup the location based on the loaded POI list
    useEffect(() => {
        if (poiList.length) {
            //console.log("Updating final location.");
            const firstPoi = poiList[0];

            // update these in case they weren't already
            if (!locationNameLong) {
                dispatch(setlocationNameLong(firstPoi.result.location.city + ", " + firstPoi.result.location.state));
            }

            if (!locationName) {
                dispatch(setlocationName(firstPoi.result.location.city + ", " + firstPoi.result.location.state));
            }
        }
    }, [poiList]);


    useEffect(() => {
        if (poiId) {
            dispatch(showPoi(poiId));
        }
        else {
            dispatch(showResults());

        }
    }, [poiId]);

    const showDynamicMap = mapoptions.dynamicMap;

    const setMapError = (status: google.maps.DirectionsStatus) => {
        if (status === google.maps.DirectionsStatus.OK) {
            setHasMapError(false);
        }
        else {
            setHasMapError(true);
        }
    }

    let rightColumn: JSX.Element;

    if (hasMapError) {
        rightColumn = <PoiListError />;
    } else if (mapDisplayMode === MapDisplayMode.START) {
        rightColumn = <RightResults isLoading={true } />;
    } else {
        rightColumn = <RightResults isLoading={false} />;
    }

    let startend: JSX.Element = null;


    if (mapDisplayMode === MapDisplayMode.RESULTS || mapDisplayMode === MapDisplayMode.POI) {
        startend = <StartEnd />;
    }

    let map: JSX.Element;
    if (showDynamicMap) {
        if (mapoptions.mapType == MapType.MAPKITJS) {
            map = <MapKitResults />;
        }
        else {
            map = <GoogleMapResults setMapError={setMapError} />;
        }
    }
    else {
        map = <GoogleMapStatic />;
    }

    const poiVisible = mapDisplayMode === MapDisplayMode.POI;
    const mapClass = poiVisible ? "map-map poi-visible" : "map-map poi-hidden";

    return (<div>
        <div className="stripe-header-controls">
            <div className="container">
                <div className="header-col">
                    <Breadcrumb />
                </div>
            </div>
        </div>
        <AdthriveLeaderboard />
        <div className="stripe-results-container">
            <div className="container">
                <HeaderSeo />
                <div className='map-grid'>
                    <div className={mapClass} >
                        <div className="sticky-container">
                            <div className="map-container">
                                {map}
                            </div>
                            {startend}
                            <AdthriveContent />
                        </div>
                    </div>
                    <div className='map-poi'>
                        <Outlet />
                    </div>
                    <div className='map-list'>
                        {rightColumn}
                    </div>
                </div>
            </div>
        </div>
        <ErrorList mapErrors={mapErrors} />
        <MetaDataResults />
        <ScrollRestoration />
    </div>);

}

export default MapContainerResults;
